/**
 * KPICard - Shared KPI Card Component
 *
 * Reusable KPI display card with support for:
 * - Accent colors (primary, emerald, blue, purple, teal, amber, red)
 * - Compact mode for space-constrained layouts (e.g., Communications Hub)
 * - Trend indicators with optional inversion
 * - Loading skeleton state
 * - Bottom accent line with hover animation
 *
 * Built on the shared Card primitive (glass surface) for consistent styling.
 *
 * Also exports KPICardRow for responsive grid layout and KPICardSkeleton.
 */

import { memo, Children } from 'react';
import { ArrowTrendingUpIcon, ArrowTrendingDownIcon } from '@heroicons/react/20/solid';
import { Card } from './Card';

// ============================================================================
// Types
// ============================================================================

export interface KPICardProps {
  label: string;
  value: string;
  subtitle?: string;
  trend?: number;
  trendLabel?: string;
  icon: React.ComponentType<{ className?: string }>;
  invertTrend?: boolean;
  loading?: boolean;
  compact?: boolean;
  accentColor?: 'primary' | 'emerald' | 'blue' | 'purple' | 'teal' | 'amber' | 'red';
  /** Optional click handler. When provided, the card renders as a button and
   *  is keyboard-focusable. Use for KPI tiles that drill into a filtered list. */
  onClick?: () => void;
  /** Render the tile dimmed (e.g., when the metric is 0 but should still be
   *  visible to keep grid layout stable). */
  dimmed?: boolean;
}

export interface KPICardSkeletonProps {
  compact?: boolean;
}

export interface KPICardRowProps {
  children: React.ReactNode;
  compact?: boolean;
  columns?: number;
}

// ============================================================================
// Accent Color Map
// ============================================================================

const accentStyles = {
  primary: {
    iconBg: 'bg-primary/10',
    iconText: 'text-primary',
    gradientFrom: 'from-primary/5',
    accentLine: 'bg-primary',
  },
  emerald: {
    iconBg: 'bg-emerald-500/10',
    iconText: 'text-emerald-400',
    gradientFrom: 'from-emerald-500/5',
    accentLine: 'bg-emerald-400',
  },
  blue: {
    iconBg: 'bg-blue-500/10',
    iconText: 'text-blue-400',
    gradientFrom: 'from-blue-500/5',
    accentLine: 'bg-blue-400',
  },
  purple: {
    iconBg: 'bg-purple-500/10',
    iconText: 'text-purple-400',
    gradientFrom: 'from-purple-500/5',
    accentLine: 'bg-purple-400',
  },
  teal: {
    iconBg: 'bg-teal-500/10',
    iconText: 'text-teal-400',
    gradientFrom: 'from-teal-500/5',
    accentLine: 'bg-teal-400',
  },
  amber: {
    iconBg: 'bg-amber-500/10',
    iconText: 'text-amber-400',
    gradientFrom: 'from-amber-500/5',
    accentLine: 'bg-amber-400',
  },
  red: {
    iconBg: 'bg-red-500/10',
    iconText: 'text-red-400',
    gradientFrom: 'from-red-500/5',
    accentLine: 'bg-red-400',
  },
};

// ============================================================================
// KPI Card Skeleton
// ============================================================================

export const KPICardSkeleton = memo(function KPICardSkeleton({
  compact = false,
}: KPICardSkeletonProps) {
  return (
    <Card
      padding={compact ? 'py-2.5 px-3' : 'p-5'}
      maxHeight=""
      overflow="overflow-visible"
      className="kpi-soft"
    >
      <div className={`flex items-start justify-between ${compact ? 'mb-1' : 'mb-4'}`}>
        <div className={`${compact ? 'h-3 w-16' : 'h-5 w-24'} bg-white/5 rounded animate-pulse`} />
        <div
          className={`${compact ? 'h-5 w-5' : 'h-8 w-8'} bg-white/5 rounded-lg animate-pulse`}
        />
      </div>
      <div
        className={`${compact ? 'h-5 w-20' : 'h-9 w-32'} bg-white/5 rounded animate-pulse ${compact ? 'mb-0.5' : 'mb-3'}`}
      />
      <div className={`${compact ? 'h-3 w-16' : 'h-4 w-28'} bg-white/5 rounded animate-pulse`} />
    </Card>
  );
});

// ============================================================================
// KPI Card Component
// ============================================================================

export const KPICard = memo(function KPICard({
  label,
  value,
  subtitle,
  trend,
  trendLabel,
  icon: Icon,
  invertTrend = false,
  loading = false,
  compact = false,
  accentColor = 'primary',
  onClick,
  dimmed = false,
}: KPICardProps) {
  if (loading) {
    return <KPICardSkeleton compact={compact} />;
  }

  const hasTrend = trend !== undefined && trend !== 0;
  const isPositive = invertTrend ? (trend ?? 0) < 0 : (trend ?? 0) > 0;
  const trendValue = Math.abs(trend ?? 0);
  const styles = accentStyles[accentColor];

  // For accent line color: use trend-based colors when there is a trend,
  // otherwise fall back to the accent color
  const accentLineClass = hasTrend
    ? isPositive
      ? 'bg-success'
      : 'bg-danger'
    : styles.accentLine;

  const cardClassName = `kpi-soft group text-left ${compact ? '' : 'h-[130px]'} ${dimmed ? 'opacity-50' : ''} ${onClick ? 'cursor-pointer' : ''}`;

  const cardContent = (
    <Card
      bareChildren
      padding={compact ? 'py-2.5 px-3' : 'p-5'}
      maxHeight=""
      overflow="overflow-visible"
      className={cardClassName}
    >
      {/* Subtle gradient overlay on hover */}
      <div
        className={`!absolute inset-0 !z-0 bg-gradient-to-br ${styles.gradientFrom} to-transparent opacity-0
                   group-hover:opacity-100 transition-opacity duration-300 pointer-events-none`}
      />

      {/* Content */}
      <div className="relative z-10">
        {/* Header: Label + Icon */}
        <div className={`flex items-start justify-between ${compact ? 'mb-1' : 'mb-0.5'}`}>
          <span className={`${compact ? 'text-[10px]' : 'text-xs'} font-semibold uppercase tracking-wider text-text-secondary`}>
            {label}
          </span>
          <div
            className={`flex items-center justify-center rounded-lg
                       ${compact ? 'w-5 h-5' : 'w-8 h-8'}
                       ${styles.iconBg} ${styles.iconText}
                       transition-transform duration-300 group-hover:scale-110`}
          >
            <Icon className={compact ? 'w-3 h-3' : 'w-4 h-4'} />
          </div>
        </div>

        {/* Value */}
        <div
          className={`font-bold text-white tracking-tight ${compact ? 'text-lg mb-0.5' : 'text-2xl mb-0.5'}`}
        >
          {value}
        </div>

        {/* Trend indicator */}
        {hasTrend && trendLabel && (
          <div className="flex items-center gap-1">
            {isPositive ? (
              <ArrowTrendingUpIcon className={`${compact ? 'w-3 h-3' : 'w-3.5 h-3.5'} text-success`} />
            ) : (
              <ArrowTrendingDownIcon className={`${compact ? 'w-3 h-3' : 'w-3.5 h-3.5'} text-danger`} />
            )}
            <span
              className={`${compact ? 'text-[10px]' : 'text-xs'} font-medium ${
                isPositive ? 'text-success' : 'text-danger'
              }`}
            >
              {isPositive ? '+' : '-'}
              {trendValue}%
            </span>
            <span className={`${compact ? 'text-[10px]' : 'text-[11px]'} text-text-tertiary ml-1`}>{trendLabel}</span>
          </div>
        )}

        {/* Show trend label without arrow when trend is 0 but label exists */}
        {!hasTrend && trendLabel && (
          <div className="flex items-center gap-1">
            <span className={`${compact ? 'text-[10px]' : 'text-[11px]'} text-text-tertiary`}>{trendLabel}</span>
          </div>
        )}

        {subtitle && (
          <div className={`${compact ? 'text-[10px] mt-1' : 'text-xs mt-1.5'} text-text-tertiary`}>
            {subtitle}
          </div>
        )}
      </div>

      {/* Bottom accent line */}
      <div
        className={`!absolute bottom-0 left-0 right-0 h-0.5 !z-[2]
                   transition-all duration-300 origin-left scale-x-0 group-hover:scale-x-100
                   ${accentLineClass}`}
      />
    </Card>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-label={label}
        className="block w-full rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
      >
        {cardContent}
      </button>
    );
  }

  return cardContent;
});

// ============================================================================
// KPI Card Row Component
// ============================================================================

const gridColsMap: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-3',
  4: 'grid-cols-4',
  5: 'grid-cols-5',
  6: 'grid-cols-6',
};

export const KPICardRow = memo(function KPICardRow({
  children,
  compact = false,
  columns,
}: KPICardRowProps) {
  const count = columns ?? Children.count(children);
  const colClass = gridColsMap[count] || 'xl:grid-cols-4';
  const gapClass = compact ? 'gap-2' : 'gap-4';

  return (
    <div className={`grid ${colClass} ${gapClass}`}>
      {children}
    </div>
  );
});

export default KPICard;
